import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, PieChart, Handshake, ArrowRight } from 'lucide-react';
import api from '../api/client';
import './Home.css';
import ProjectCard from './ProjectCard';

const Home = () => {
  const navigate = useNavigate();
  const [spvs, setSpvs] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    api.get('/api/spvs/public/')
      .then((res) => setSpvs(res.data))
      .catch(() => { setSpvs([]); setLoadError(true); });
  }, []);

  return (
    <div className="homeContainer">
      {/* Hero Section */}
      <section className="hero">
        <div className="heroBg">
          <img src="/hero-land-investment.jpg" alt="" className="heroImage" />
          <div className="heroOverlay"></div>
        </div>
        <div className="heroContent">
          <div className="heroLeft space-y-8">
                        <h1 className="heroTitle">
              Invest Fractionally in <span className="heroHighlight">Delhi Land Parcels</span>
            </h1>
            <p className="heroDescription">
              Take part in individual land projects in Delhi, each held in its own company (SPV). Participation is by invitation.
            </p>
            <div className="ctaButtons">
              <button onClick={() => navigate('/contact')} className="btn-primary px-8 py-4 text-base font-semibold flex items-center">
                Request an invite
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
              <button onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })} className="learnMoreButton px-8 py-4 text-base font-semibold">How it works</button>
            </div>
            </div>
          <div className="heroRight">
            <div className="statsCard bg-card/60 p-6 rounded-2xl border border-border">
              <h2 className="text-lg font-semibold text-foreground mb-4">Current opportunities</h2>
              <p className="text-sm text-muted-foreground">
                {spvs === null ? 'Loading…' : spvs.length === 0 ? 'No offering is open right now.' : `${spvs.length} open for invited investors.`}
              </p>
              <div className="mt-4 flex gap-3">
                <button onClick={() => navigate('/contact')} className="btn-primary flex-1">Request an invite</button>
                <button onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })} className="btn-outline flex-1">How It Works</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="howItWorks">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h2 className="sectionTitle">
              How Fractional Ownership Works
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Simple, transparent, and designed for every investor.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="step group relative bg-card/50 backdrop-blur-sm rounded-2xl p-8 border border-border/50 hover:border-primary/20 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              {/* Step Number */}
              <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                1
              </div>
              
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                <MapPin className="w-8 h-8 text-primary group-hover:scale-110 transition-transform duration-300" />
              </div>
              
              {/* Content */}
              <h3 className="stepTitle">
                Choose Land Parcel
              </h3>
              <p className="stepDesc">
                Select a premium land parcel listed on our platform, backed by legal and due diligence.
              </p>
            </div>
            <div className="step group relative bg-card/50 backdrop-blur-sm rounded-2xl p-8 border border-border/50 hover:border-primary/20 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              {/* Step Number */}
              <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                2
              </div>
              
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                <PieChart className="w-8 h-8 text-primary group-hover:scale-110 transition-transform duration-300" />
              </div>
              
              {/* Content */}
              <h3 className="stepTitle">
                Buy Shares
              </h3>
              <p className="stepDesc">
                Invest by buying shares in the project's company, within the offer limits.
              </p>
            </div>
            <div className="step group relative bg-card/50 backdrop-blur-sm rounded-2xl p-8 border border-border/50 hover:border-primary/20 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              {/* Step Number */}
              <div className="absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                3
              </div>
              
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300">
                <Handshake className="w-8 h-8 text-primary group-hover:scale-110 transition-transform duration-300" />
              </div>
              
              {/* Content */}
              <h3 className="stepTitle">
                Earn & Exit
              </h3>
              <p className="stepDesc">
                Sell your fractions or exit when land is sold to developers, DDA, or builders. Profits are distributed fairly.
              </p>
            </div>
          </div>

          </div>
      </section>

      {/* Featured Projects */}
      <section className="featuredProjects">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="sectionTitle">
              Open offerings
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Land projects open to invited investors
            </p>
          </div>

          {/* Projects Grid (live from the API) */}
          {spvs === null && <p className="text-center text-muted-foreground">Loading projects…</p>}
          {loadError && <p className="text-center text-muted-foreground" role="alert">Projects could not be loaded. Please try again later.</p>}
          {spvs && spvs.length === 0 && !loadError && (
            <p className="text-center text-muted-foreground">No offering is open right now. <a href="/contact">Request an invite</a> to be told about the next one.</p>
          )}
          {spvs && spvs.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {spvs.map((spv) => <ProjectCard key={spv.id} spv={spv} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
